import { Controller } from "@hotwired/stimulus";

// Mass mapping for Activity Class Mapping: row selection, bulk actions, confirmation modal.
export default class extends Controller {
  static targets = [
    "row", "bulkBar", "selectedCount", "modal", "previewSourceCount", "previewRowCount",
    "programModal", "previewSourceCountProgram", "previewRowCountProgram", "acceptAllFeedback"
  ];

  connect() {
    this.updateUI();
  }

  // When Map Type radio changes, show/hide the correct Target selector (timeslot vs program)
  onMapTypeChange(e) {
    const radio = e.currentTarget;
    const row = radio.closest("tr");
    if (!row) return;
    const mapType = radio.value;
    const timeslotDiv = row.querySelector("[data-target-timeslot]");
    const programDiv = row.querySelector("[data-target-program]");
    const timeslotHidden = timeslotDiv?.querySelector('input[name*="timeslot_schedule_id"]');
    const programHidden = programDiv?.querySelector('input[name*="program_id"]');
    const timeslotInput = timeslotDiv?.querySelector('[data-forms--combobox-target="input"]');
    const programInput = programDiv?.querySelector('[data-forms--combobox-target="input"]');

    if (mapType === "timeslot") {
      if (timeslotDiv) timeslotDiv.style.display = "";
      if (programDiv) programDiv.style.display = "none";
      if (programHidden) programHidden.value = "";
      if (programInput) programInput.value = "";
    } else if (mapType === "program") {
      if (timeslotDiv) timeslotDiv.style.display = "none";
      if (programDiv) programDiv.style.display = "";
      if (timeslotHidden) timeslotHidden.value = "";
      if (timeslotInput) timeslotInput.value = "";
    } else {
      if (timeslotDiv) timeslotDiv.style.display = "none";
      if (programDiv) programDiv.style.display = "none";
      if (timeslotHidden) timeslotHidden.value = "";
      if (programHidden) programHidden.value = "";
      if (timeslotInput) timeslotInput.value = "";
      if (programInput) programInput.value = "";
    }
  }

  toggleRow(e) {
    const checkbox = e.currentTarget;
    const row = checkbox.closest("tr");
    if (row) {
      row.classList.toggle("bg-blue-50", checkbox.checked);
    }
    this.updateUI();
  }

  selectAll() {
    const rows = this.rowTargets;
    const allChecked = rows.length > 0 && rows.every((r) => r.querySelector("[data-select-checkbox]")?.checked);
    rows.forEach((row) => {
      const cb = row.querySelector("[data-select-checkbox]");
      if (cb) {
        cb.checked = !allChecked;
        row.classList.toggle("bg-blue-50", cb.checked);
      }
    });
    this.updateUI();
  }

  clearSelection() {
    this.rowTargets.forEach((row) => {
      const cb = row.querySelector("[data-select-checkbox]");
      if (cb) {
        cb.checked = false;
        row.classList.remove("bg-blue-50");
      }
    });
    this.updateUI();
  }

  selectFiltered() {
    this.clearSelection();
    const q = (this.element.querySelector("[data-filter-input]")?.value || "").trim().toLowerCase();
    this.rowTargets.forEach((row) => {
      const text = (row.querySelector("[data-source-class]")?.textContent || "").trim().toLowerCase();
      if (!q || text.includes(q)) {
        const cb = row.querySelector("[data-select-checkbox]");
        if (cb) {
          cb.checked = true;
          row.classList.add("bg-blue-50");
        }
      }
    });
    this.updateUI();
  }

  selectHighConfidence() {
    this.clearSelection();
    this.rowTargets.forEach((row) => {
      if (row.querySelector("[data-high-confidence]")) {
        const cb = row.querySelector("[data-select-checkbox]");
        if (cb) {
          cb.checked = true;
          row.classList.add("bg-blue-50");
        }
      }
    });
    this.updateUI();
  }

  acceptAllHighConfidence() {
    const mappedCount = [];
    this.rowTargets.forEach((row) => {
      const scheduleId = row.dataset.autoApplyScheduleId;
      const scheduleLabel = row.dataset.autoApplyScheduleLabel;
      if (!scheduleId || !scheduleLabel) return;

      // Skip if already mapped (has value in timeslot field)
      const hidden = row.querySelector('input[type="hidden"][name*="timeslot_schedule_id"]');
      const ignoreCb = row.querySelector("[data-ignore-checkbox]");
      if (ignoreCb?.checked) return; // Skip ignored rows
      if (hidden?.value && hidden.value !== "" && hidden.value !== "__ignored__") return; // Skip already mapped

      // Apply suggested timeslot
      if (hidden) hidden.value = scheduleId;
      const textInput = row.querySelector('[data-forms--combobox-target="input"]');
      if (textInput) textInput.value = scheduleLabel;

      if (ignoreCb) ignoreCb.checked = false;

      // Update status cell to Mapped
      const statusCell = row.querySelector("[data-row-status]");
      if (statusCell) {
        statusCell.innerHTML =
          '<span class="px-2 py-0.5 rounded text-xs font-medium" style="background: #d1fae5; color: #065f46;">Mapped</span>';
      }

      // Remove data-auto-apply so we don't double-apply on subsequent clicks
      delete row.dataset.autoApplyScheduleId;
      delete row.dataset.autoApplyScheduleLabel;

      mappedCount.push(row);
    });

    // Show success feedback
    if (this.hasAcceptAllFeedbackTarget) {
      const n = mappedCount.length;
      this.acceptAllFeedbackTarget.textContent =
        n > 0 ? `Applied ${n} high-confidence mapping${n === 1 ? "" : "s"}.` : "No unmapped high-confidence rows to apply.";
      this.acceptAllFeedbackTarget.classList.remove("hidden");
      setTimeout(() => this.acceptAllFeedbackTarget.classList.add("hidden"), 4000);
    }
  }

  openBulkMapModal() {
    const { sourceCount, rowCount } = this.selectionStats();
    if (this.hasPreviewSourceCountTarget) this.previewSourceCountTarget.textContent = sourceCount;
    if (this.hasPreviewRowCountTarget) this.previewRowCountTarget.textContent = rowCount;
    if (this.hasModalTarget) this.modalTarget.classList.remove("hidden");
  }

  closeModal() {
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden");
  }

  openBulkMapProgramModal() {
    const { sourceCount, rowCount } = this.selectionStats();
    if (this.hasPreviewSourceCountProgramTarget)
      this.previewSourceCountProgramTarget.textContent = sourceCount;
    if (this.hasPreviewRowCountProgramTarget)
      this.previewRowCountProgramTarget.textContent = rowCount;
    if (this.hasProgramModalTarget) this.programModalTarget.classList.remove("hidden");
  }

  closeProgramModal() {
    if (this.hasProgramModalTarget) this.programModalTarget.classList.add("hidden");
  }

  confirmBulkMap(e) {
    e.preventDefault();
    const form = this.element.querySelector("#bulk_map_form");
    const hidden = form?.querySelector('input[name="bulk_timeslot_schedule_id"]');
    if (!hidden?.value) {
      alert("Please select a timeslot.");
      return;
    }
    // Inject selected source names into form
    const container = form.querySelector("[data-bulk-names-container]");
    if (container) {
      container.innerHTML = "";
      this.getSelectedSourceNames().forEach((name) => {
        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = "bulk_source_names[]";
        inp.value = name;
        container.appendChild(inp);
      });
    }
    form.submit();
  }

  confirmBulkMapProgram(e) {
    e.preventDefault();
    const form = this.element.querySelector("#bulk_map_program_form");
    const hidden = form?.querySelector('input[name="bulk_program_id"]');
    if (!hidden?.value) {
      alert("Please select a program.");
      return;
    }
    const container = form.querySelector("[data-bulk-names-container-program]");
    if (container) {
      container.innerHTML = "";
      this.getSelectedSourceNames().forEach((name) => {
        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = "bulk_source_names[]";
        inp.value = name;
        container.appendChild(inp);
      });
    }
    form.submit();
  }

  getSelectedSourceNames() {
    const names = [];
    this.rowTargets.forEach((row) => {
      const cb = row.querySelector("[data-select-checkbox]");
      if (cb?.checked) {
        const name = row.dataset.sourceClassName;
        if (name) names.push(name);
      }
    });
    return names;
  }

  selectionStats() {
    let sourceCount = 0;
    let rowCount = 0;
    this.rowTargets.forEach((row) => {
      const cb = row.querySelector("[data-select-checkbox]");
      if (cb?.checked) {
        sourceCount += 1;
        rowCount += parseInt(row.dataset.activityCount || "0", 10);
      }
    });
    return { sourceCount, rowCount };
  }

  injectBulkNamesBeforeSubmit(e) {
    const names = this.getSelectedSourceNames();
    if (names.length === 0) {
      alert("Please select at least one row.");
      e.preventDefault();
      return;
    }
    const form = e.target;
    if (form && form.tagName === "FORM") {
      let container = form.querySelector("[data-bulk-names-container]");
      if (!container) {
        container = document.createElement("div");
        container.setAttribute("data-bulk-names-container", "");
        container.style.display = "none";
        form.appendChild(container);
      }
      container.innerHTML = "";
      names.forEach((name) => {
        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = "bulk_source_names[]";
        inp.value = name;
        container.appendChild(inp);
      });
    }
  }

  updateUI() {
    const { sourceCount } = this.selectionStats();
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = sourceCount;
    }
    if (this.hasBulkBarTarget) {
      this.bulkBarTarget.classList.toggle("hidden", sourceCount === 0);
    }
  }
}
